"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { File as SpotFile } from "@/types";
import { FileUpload } from "@/components/Form/FileInputField";
import type { FileTypes } from "@/utils/mimetypes";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";

import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { BindableInput } from "./BindableInput";
import { cn } from "@/lib/utils";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";

/**
 * V2 renderer for `display=ConfigBindableFile`. The literal mode is the
 * standard file picker (upload / select from library) via {@link FileUpload};
 * the "bind to variable" braces button (and the variable chip when bound) are
 * layered on by {@link BindableInput}. Storage is `File | RefToken` — a
 * SpotFile object (or list) when uploaded, or `:var[<ref>]` when bound.
 */
export function BindableFileField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  isList,
  allowedFileTypes,
  isRequired,
  showOptionalLabel,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  isList?: boolean;
  allowedFileTypes?: FileTypes[];
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const isRef = typeof raw === "string" && WHOLE_VAR_REF_PATTERN.test(raw);
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;

  const setValue = (next: unknown) =>
    form.setValue(path, next, { shouldDirty: true, shouldTouch: true });

  // Current uploaded file(s) normalised to SpotFile[] for the uploader.
  const values: SpotFile[] = useMemo(() => {
    if (isRef || raw == null || raw === "") return [];
    if (Array.isArray(raw)) return raw as SpotFile[];
    if (typeof raw === "object" && "uri" in raw) return [raw as SpotFile];
    return [];
  }, [raw, isRef]);

  const literal = (
    <FileUpload
      variant="inline"
      values={values}
      isDisabled={disabled}
      canSelectMultipleFiles={!!isList}
      showSelectedFiles
      allowed_file_types={allowedFileTypes}
      // Single-file fields store one SpotFile (not a 1-element list) so the
      // backend `File | None` validates; list fields store the array.
      setValues={(files) => setValue(isList ? files : (files[0] ?? null))}
    />
  );

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-warning-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          dataType="File"
          inputName={fieldKey}
          value={isRef ? (raw as string) : ""}
          disabled={disabled}
          renderLiteral={literal}
          onChange={(next) => setValue(next)}
        />
      </div>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
